import { forwardRef } from "react";
import { PALETTE } from "@/components/MindMap";
import { StudySheet, SHEET_W, SHEET_H } from "@/components/StudySheet";
import { fmtDate } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";

const Header = ({ work, title, emoji, label }) => (
  <header className="flex items-end justify-between border-b-4 border-zinc-950 pb-3">
    <div className="flex items-center gap-4">
      <span className="text-5xl leading-none">{emoji}</span>
      <div>
        <p className="font-mono text-[12px] uppercase tracking-widest text-[#0D9488]">{label}</p>
        <h1 className="font-display text-[38px] font-extrabold tracking-tight leading-none mt-1">{title}</h1>
      </div>
    </div>
    <div className="text-right font-mono text-[11px] uppercase tracking-widest text-zinc-500 leading-relaxed">
      <div className="flex items-center justify-end gap-2.5 mb-1"><img src={`${process.env.PUBLIC_URL}/logo-app.png`} alt="Aurora School" data-testid="sheet-logo" style={{ width: 46, height: 46, borderRadius: 10 }} /><p className="font-display text-[18px] font-extrabold tracking-tight normal-case text-zinc-950">Aurora School</p></div>
      <p>{[work.author, work.classe, fmtDate(work.created_at || new Date())].filter(Boolean).join(" · ")}</p>
    </div>
  </header>
);

export const SheetB = forwardRef(({ work }, ref) => {
  const v = work.version_b || {};
  return (
    <div ref={ref} className="bg-white text-zinc-950 flex flex-col" style={{ width: SHEET_W, height: SHEET_H, padding: 28 }}>
      <Header work={work} title={work.title || v.title} emoji={v.emoji} label="Versione B · Sintesi XL" />
      <div className="flex items-center gap-6 py-5">
        <span className="text-[96px] leading-none">{v.emoji}</span>
        <p className="font-display text-[34px] font-bold leading-tight tracking-tight flex-1">{v.tagline}</p>
      </div>
      <div className="grid grid-cols-3 grid-rows-2 gap-4 flex-1">
        {(v.concepts || []).map((c, i) => {
          const col = PALETTE[i % PALETTE.length];
          return (
            <div key={`${c.title}-${i}`} className="flex flex-col justify-center px-6 py-4" style={{ background: col.bg, borderTop: `10px solid ${col.c}` }}>
              <div className="flex items-center gap-4 mb-3">
                <span className="text-[72px] leading-none">{c.emoji}</span>
                <span className="font-mono text-[18px] font-bold" style={{ color: col.c }}>0{i + 1}</span>
              </div>
              <p className="font-display text-[34px] font-extrabold leading-[1.05] tracking-tight" style={{ color: col.c }}>{c.title}</p>
              <p className="text-[22px] leading-snug text-zinc-800 mt-2">{c.text}</p>
            </div>
          );
        })}
      </div>
      <div className="mt-4 bg-zinc-950 text-white px-7 py-5 flex items-center gap-5">
        <span className="font-mono text-[13px] uppercase tracking-widest text-[#5EEAD4] shrink-0">Da ricordare</span>
        <p className="font-display text-[28px] font-bold leading-tight">{v.takeaway}</p>
      </div>
    </div>
  );
});
SheetB.displayName = "SheetB";

const Panel = ({ p, i }) => (
  <div className="relative overflow-hidden bg-zinc-100" style={{ border: "5px solid #09090B" }}>
    {p.image_path ? (
      <AuthImage path={p.image_path} alt={p.caption} className="absolute inset-0 w-full h-full object-cover" />
    ) : (
      <div className="absolute inset-0 grid place-items-center text-[120px]">{p.emoji}</div>
    )}
    <div className="absolute top-0 left-0 max-w-[70%] bg-[#FEF08A] px-4 py-2.5 text-[17px] font-semibold leading-snug" style={{ borderRight: "4px solid #09090B", borderBottom: "4px solid #09090B" }}>
      <span className="inline-grid place-items-center w-7 h-7 mr-2 bg-[#0D9488] text-white font-mono text-[14px] font-bold align-middle">{i + 1}</span>
      {p.caption}
    </div>
    {p.dialogue && (
      <div className="absolute bottom-5 right-5 max-w-[60%]">
        <div className="relative bg-white px-5 py-3 text-[19px] font-bold leading-snug" style={{ border: "4px solid #09090B", borderRadius: 28 }}>
          <span className="mr-1">{p.emoji}</span>{p.dialogue}
          <span className="absolute -bottom-[14px] left-10 w-6 h-6 bg-white" style={{ borderRight: "4px solid #09090B", borderBottom: "4px solid #09090B", transform: "rotate(45deg)" }} />
        </div>
      </div>
    )}
  </div>
);

export const SheetC = forwardRef(({ work }, ref) => {
  const v = work.version_c || {};
  return (
    <div ref={ref} className="bg-white text-zinc-950 flex flex-col" style={{ width: SHEET_W, height: SHEET_H, padding: 28 }}>
      <Header work={work} title={v.title || work.title} emoji={v.emoji} label="Versione C · Manga di studio" />
      <div className="grid grid-cols-2 grid-rows-2 gap-4 flex-1 mt-4">
        {(v.panels || []).map((p, i) => <Panel key={`${p.caption}-${i}`} p={p} i={i} />)}
      </div>
      <div className="mt-4 flex items-stretch" style={{ border: "5px solid #09090B" }}>
        <span className="bg-[#0D9488] text-white font-display font-extrabold text-[22px] px-6 grid place-items-center tracking-tight">LA LEZIONE</span>
        <p className="font-display text-[26px] font-bold leading-tight px-6 py-4 flex-1">{v.lesson}</p>
      </div>
    </div>
  );
});
SheetC.displayName = "SheetC";

export const VERSIONS = [
  { id: "a", key: "mindmap", label: "Versione A", sub: "Mappa completa" },
  { id: "b", key: "version_b", label: "Versione B", sub: "Sintesi XL" },
  { id: "c", key: "version_c", label: "Versione C", sub: "Manga" },
];

export const VersionSheet = forwardRef(({ work, version, still = true }, ref) =>
  version === "b" ? <SheetB ref={ref} work={work} /> : version === "c" ? <SheetC ref={ref} work={work} /> : <StudySheet ref={ref} work={work} still={still} />
);
VersionSheet.displayName = "VersionSheet";
